import { useCallback, useState } from 'react'

import type { UseAgentCreateFormResult } from 'features/trend-detection/features/agent-create/hooks/use-agent-create-form'
import type { TrendDetectionService } from 'features/trend-detection/service/service'
import type { IssueAgentResponse } from 'features/trend-detection/types'
import { buildAgentFiltersV2 } from 'features/trend-detection/utils/agent-filters-v2'

export interface UseAgentUpdateSubmitParams {
  service: TrendDetectionService
  projectId: string | null
  agentId: string | null
  form: UseAgentCreateFormResult
}

export interface UseAgentUpdateSubmitResult {
  saveAndRun: () => Promise<IssueAgentResponse>
  isSaving: boolean
}

/** Issue Agent's edit-mode "Save and run": PATCHes the existing agent with
 * the create form's current field values, then re-runs it. Mirrors
 * `useAgentCreateSubmit`'s create-and-run, but against `updateAgent` — the
 * PATCH endpoint the deleted `useAgentEdit` hook used to own. */
export function useAgentUpdateSubmit({
  service,
  projectId,
  agentId,
  form,
}: UseAgentUpdateSubmitParams): UseAgentUpdateSubmitResult {
  const [isSaving, setIsSaving] = useState(false)

  const saveAndRun = useCallback(async (): Promise<IssueAgentResponse> => {
    if (!projectId) throw new Error('No project selected')
    if (!agentId) throw new Error('No agent to save')
    if (!form.agentName.trim()) throw new Error('Please enter an agent name')

    setIsSaving(true)
    try {
      const criteria = await form.ensureFiltersBeforeSubmit()
      const agent = await service.updateAgent(projectId, agentId, {
        name: form.agentName.trim(),
        description: form.prompt.trim() || undefined,
        config: {
          prompt: form.prompt.trim() || form.agentName.trim(),
          filters_v2: buildAgentFiltersV2(criteria),
        },
        sensitivity: form.sensitivity,
        reinterpret_filters: false,
      })
      await service.updateEmailPreference(
        projectId,
        agentId,
        form.emailNotifications,
      )
      await service.runAgent(projectId, agentId, {})
      return agent
    } finally {
      setIsSaving(false)
    }
  }, [projectId, agentId, form, service])

  return { saveAndRun, isSaving }
}
